import React, { useEffect, useState } from 'react';
import { Smartphone, Download, Share, Plus, Apple, Chrome, CheckCircle2, ArrowRight, MapPin, Bell, ShieldCheck, AlertTriangle } from 'lucide-react';

const InstallApp = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [platform, setPlatform] = useState('android'); // android | ios | desktop

  // Permission states: 'unknown' | 'granted' | 'denied' | 'requesting'
  const [locPerm, setLocPerm] = useState('unknown');
  const [notifPerm, setNotifPerm] = useState('unknown');
  const [requestingAll, setRequestingAll] = useState(false);

  // Read current permission states on mount
  useEffect(() => {
    (async () => {
      try {
        if (navigator.permissions && navigator.permissions.query) {
          const p1 = await navigator.permissions.query({ name: 'geolocation' });
          setLocPerm(p1.state);
          p1.onchange = () => setLocPerm(p1.state);
        }
      } catch { /* ignore */ }
      if ('Notification' in window) {
        const s = Notification.permission;
        setNotifPerm(s === 'default' ? 'unknown' : s);
      }
    })();
  }, []);

  // Request both Location + Notifications in sequence
  const requestAllPermissions = async () => {
    setRequestingAll(true);
    // 1. Location
    if (navigator.geolocation) {
      setLocPerm('requesting');
      await new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
          () => { setLocPerm('granted'); resolve(); },
          (err) => { setLocPerm(err.code === 1 ? 'denied' : 'unknown'); resolve(); },
          { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
        );
      });
    }
    // 2. Notifications
    if ('Notification' in window) {
      setNotifPerm('requesting');
      try {
        const result = await Notification.requestPermission();
        setNotifPerm(result === 'default' ? 'unknown' : result);
      } catch { setNotifPerm('unknown'); }
    }
    setRequestingAll(false);
  };

  useEffect(() => {
    const ua = navigator.userAgent || '';
    if (/iPad|iPhone|iPod/.test(ua)) setPlatform('ios');
    else if (/Android/i.test(ua)) setPlatform('android');
    else setPlatform('desktop');

    // Detect if app already running as installed PWA
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
      setInstalled(true);
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => {
      setInstalled(true);
      setDeferredPrompt(null);
      // After install — auto-prompt permissions
      setTimeout(() => requestAllPermissions(), 500);
    });
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    if (choice.outcome === 'accepted') {
      setInstalled(true);
      // Auto-request permissions immediately
      setTimeout(() => requestAllPermissions(), 500);
    }
    setDeferredPrompt(null);
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="install-app-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-emerald-900 via-stone-900 to-stone-800 py-16 px-6">
        <div className="max-w-4xl mx-auto text-center text-white">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-500/20 text-amber-300 rounded-full text-xs font-semibold uppercase tracking-widest mb-6">
            <Smartphone className="w-3 h-3" /> Mobile App
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-playfair font-bold mb-4">
            Install DBK on your phone
          </h1>
          <p className="text-stone-300 text-lg max-w-2xl mx-auto mb-8">
            Get the DBK Group app on your iPhone or Android home screen — works like a native app, full screen, offline-ready, no app store needed.
          </p>

          {installed ? (
            <div
              data-testid="install-already-installed"
              className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-700 text-white rounded font-bold"
            >
              <CheckCircle2 className="w-5 h-5" /> Already installed — open from your home screen
            </div>
          ) : deferredPrompt ? (
            <button
              onClick={handleInstallClick}
              data-testid="install-prompt-button"
              className="inline-flex items-center gap-2 px-8 py-4 bg-amber-500 hover:bg-amber-600 text-white rounded font-bold uppercase tracking-widest text-sm transition-colors"
            >
              <Download className="w-5 h-5" /> Install App Now
            </button>
          ) : (
            <p className="text-stone-400 text-sm">
              Follow the steps below to install on your device →
            </p>
          )}
        </div>
      </section>

      {/* App Permissions Section */}
      <section className="py-10 px-6 max-w-3xl mx-auto" data-testid="app-permissions-section">
        <div className="bg-white border-2 border-amber-200 rounded-lg p-6 shadow-md">
          <div className="flex items-start gap-4 mb-5">
            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-6 h-6 text-amber-700" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-playfair font-bold text-stone-900 mb-1">
                Step 1 — Grant App Permissions
              </h2>
              <p className="text-stone-600 text-sm">
                The DBK CRM needs these permissions to work properly. Tap the button below — your browser will ask for each one. <strong>Click "Allow"</strong> for everything.
              </p>
            </div>
          </div>

          {/* Permission cards */}
          <div className="space-y-3 mb-5">
            {/* Location */}
            <div className="flex items-center gap-3 p-3 bg-stone-50 rounded border border-stone-200">
              <MapPin className="w-5 h-5 text-purple-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-bold text-stone-900 text-sm">Location Access <span className="text-red-600">*</span></p>
                <p className="text-xs text-stone-500">Required — HR uses this to track field employees</p>
              </div>
              {locPerm === 'granted' && (
                <span className="flex items-center gap-1 text-xs font-bold text-green-700 bg-green-100 px-2 py-1 rounded">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Granted
                </span>
              )}
              {locPerm === 'denied' && (
                <span className="flex items-center gap-1 text-xs font-bold text-red-700 bg-red-100 px-2 py-1 rounded">
                  <AlertTriangle className="w-3.5 h-3.5" /> Blocked
                </span>
              )}
              {locPerm === 'requesting' && (
                <span className="text-xs font-bold text-blue-700 bg-blue-100 px-2 py-1 rounded">Requesting…</span>
              )}
              {(locPerm === 'unknown' || locPerm === 'prompt') && (
                <span className="text-xs text-stone-400">Not granted</span>
              )}
            </div>

            {/* Notifications */}
            <div className="flex items-center gap-3 p-3 bg-stone-50 rounded border border-stone-200">
              <Bell className="w-5 h-5 text-orange-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-bold text-stone-900 text-sm">Notifications <span className="text-red-600">*</span></p>
                <p className="text-xs text-stone-500">Required — HR alerts, task assignments, circulars</p>
              </div>
              {notifPerm === 'granted' && (
                <span className="flex items-center gap-1 text-xs font-bold text-green-700 bg-green-100 px-2 py-1 rounded">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Granted
                </span>
              )}
              {notifPerm === 'denied' && (
                <span className="flex items-center gap-1 text-xs font-bold text-red-700 bg-red-100 px-2 py-1 rounded">
                  <AlertTriangle className="w-3.5 h-3.5" /> Blocked
                </span>
              )}
              {notifPerm === 'requesting' && (
                <span className="text-xs font-bold text-blue-700 bg-blue-100 px-2 py-1 rounded">Requesting…</span>
              )}
              {(notifPerm === 'unknown' || notifPerm === 'prompt') && (
                <span className="text-xs text-stone-400">Not granted</span>
              )}
            </div>
          </div>

          {/* CTA */}
          {(locPerm === 'granted' && notifPerm === 'granted') ? (
            <div
              data-testid="all-permissions-granted"
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded font-bold"
            >
              <CheckCircle2 className="w-5 h-5" /> All permissions granted — you're all set!
            </div>
          ) : (
            <button
              onClick={requestAllPermissions}
              disabled={requestingAll}
              data-testid="grant-all-permissions-btn"
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-emerald-700 hover:bg-emerald-800 text-white rounded font-bold uppercase tracking-widest text-sm transition-colors disabled:opacity-60"
            >
              <ShieldCheck className="w-5 h-5" />
              {requestingAll ? 'Requesting Permissions…' : 'Grant All Permissions'}
            </button>
          )}

          {/* If denied — show recovery instructions */}
          {(locPerm === 'denied' || notifPerm === 'denied') && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-xs text-red-800">
              <p className="font-bold mb-1 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Permissions blocked?</p>
              <p>
                Tap the <strong>🔒 lock icon</strong> in your browser address bar → <strong>Permissions</strong> → set Location and Notifications to <strong>Allow</strong> → then refresh this page.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Platform Tabs */}
      <section className="py-10 px-6 max-w-4xl mx-auto">
        <div className="flex justify-center gap-2 mb-10">
          {[
            { key: 'ios', label: 'iPhone / iPad', icon: Apple },
            { key: 'android', label: 'Android', icon: Chrome },
            { key: 'desktop', label: 'Desktop', icon: Chrome },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              data-testid={`platform-tab-${key}`}
              onClick={() => setPlatform(key)}
              className={`flex items-center gap-2 px-5 py-3 rounded font-bold text-sm uppercase tracking-widest border-2 transition-all ${
                platform === key
                  ? 'bg-emerald-900 text-white border-emerald-900'
                  : 'bg-white text-stone-600 border-stone-200 hover:border-emerald-900'
              }`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>

        {/* iOS Instructions */}
        {platform === 'ios' && (
          <div className="bg-white border border-stone-200 rounded-lg p-8 space-y-6" data-testid="ios-instructions">
            <h2 className="text-2xl font-playfair font-bold text-stone-900 flex items-center gap-3">
              <Apple className="w-6 h-6" /> iPhone / iPad (Safari)
            </h2>
            <ol className="space-y-5">
              {[
                ['Open this site in Safari', 'Make sure you are using Safari — Chrome on iOS cannot install web apps.'],
                ['Tap the Share button', <>Tap the <Share className="inline w-4 h-4 mx-1" /> Share icon at the bottom of the screen.</>],
                ['Tap "Add to Home Screen"', <>Scroll down in the share sheet and tap <Plus className="inline w-4 h-4 mx-1" /> "Add to Home Screen".</>],
                ['Confirm', 'Confirm the name "DBK Group" and tap "Add" at the top right.'],
                ['Launch the app', 'Find the DBK icon on your home screen and tap to open — it now runs full screen, like a native app.'],
              ].map(([title, body], i) => (
                <li key={i} className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-emerald-900 text-white font-bold flex items-center justify-center flex-shrink-0">
                    {i + 1}
                  </div>
                  <div>
                    <p className="font-bold text-stone-900 mb-1">{title}</p>
                    <p className="text-stone-600 text-sm">{body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Android Instructions */}
        {platform === 'android' && (
          <div className="bg-white border border-stone-200 rounded-lg p-8 space-y-6" data-testid="android-instructions">
            <h2 className="text-2xl font-playfair font-bold text-stone-900 flex items-center gap-3">
              <Chrome className="w-6 h-6" /> Android (Chrome / Edge)
            </h2>
            <ol className="space-y-5">
              {[
                ['Open this site in Chrome', 'Use Chrome, Edge, or Samsung Internet — recommended for best PWA support.'],
                ['Tap the "Install App" banner', 'If you see a popup at the bottom or an "Install" option, tap it. Otherwise continue with step 3.'],
                ['Open the browser menu', 'Tap the three-dot ⋮ menu at the top right of Chrome.'],
                ['Tap "Install app" or "Add to Home screen"', 'You\'ll see one of these options near the bottom of the menu.'],
                ['Confirm', 'Tap "Install". The DBK icon will appear on your home screen and app drawer.'],
              ].map(([title, body], i) => (
                <li key={i} className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-emerald-900 text-white font-bold flex items-center justify-center flex-shrink-0">
                    {i + 1}
                  </div>
                  <div>
                    <p className="font-bold text-stone-900 mb-1">{title}</p>
                    <p className="text-stone-600 text-sm">{body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Desktop Instructions */}
        {platform === 'desktop' && (
          <div className="bg-white border border-stone-200 rounded-lg p-8 space-y-6" data-testid="desktop-instructions">
            <h2 className="text-2xl font-playfair font-bold text-stone-900 flex items-center gap-3">
              <Chrome className="w-6 h-6" /> Desktop (Chrome / Edge)
            </h2>
            <ol className="space-y-5">
              {[
                ['Open this site in Chrome or Edge', 'These browsers fully support installing web apps to desktop.'],
                ['Look for the install icon in the address bar', 'You\'ll see a small computer/download icon on the right side of the URL bar.'],
                ['Click "Install"', 'Or open the three-dot menu → "Install DBK Group".'],
                ['Launch from desktop', 'The app gets a desktop shortcut, taskbar entry, and opens in its own window.'],
              ].map(([title, body], i) => (
                <li key={i} className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-emerald-900 text-white font-bold flex items-center justify-center flex-shrink-0">
                    {i + 1}
                  </div>
                  <div>
                    <p className="font-bold text-stone-900 mb-1">{title}</p>
                    <p className="text-stone-600 text-sm">{body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Features */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-12">
          {[
            ['Full Screen', 'No browser bars — just your CRM.'],
            ['Offline Shell', 'Opens instantly even on poor network.'],
            ['Home Screen Icon', 'DBK logo on your home screen.'],
          ].map(([t, d]) => (
            <div key={t} className="bg-white border border-stone-200 p-5 rounded">
              <CheckCircle2 className="w-5 h-5 text-emerald-700 mb-2" />
              <p className="font-bold text-stone-900 mb-1">{t}</p>
              <p className="text-stone-600 text-sm">{d}</p>
            </div>
          ))}
        </div>

        {/* Direct links */}
        <div className="mt-12 bg-stone-900 text-white p-8 rounded">
          <p className="text-xs uppercase tracking-widest text-amber-400 mb-2">Share with your team</p>
          <h3 className="text-2xl font-playfair font-bold mb-4">Direct install link</h3>
          <p className="text-stone-300 mb-4">
            Send this URL to anyone in your team — they open it on their phone and follow the steps:
          </p>
          <div className="bg-stone-800 px-4 py-3 rounded font-mono text-sm break-all" data-testid="install-link">
            https://siddhivinayakinfra.estate/install
          </div>
          <button
            type="button"
            data-testid="copy-install-link-button"
            onClick={() => {
              navigator.clipboard.writeText('https://siddhivinayakinfra.estate/install');
            }}
            className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-stone-900 rounded text-xs font-bold uppercase tracking-widest transition-colors"
          >
            Copy Link
          </button>
          <div className="mt-6 flex flex-wrap gap-3">
            <a
              href="/dashboard"
              className="inline-flex items-center gap-2 px-5 py-3 bg-emerald-700 hover:bg-emerald-600 rounded text-sm font-bold uppercase tracking-widest transition-colors"
              data-testid="open-crm-button"
            >
              Open CRM <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="/"
              className="inline-flex items-center gap-2 px-5 py-3 bg-stone-700 hover:bg-stone-600 rounded text-sm font-bold uppercase tracking-widest transition-colors"
              data-testid="open-website-button"
            >
              Open Website <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InstallApp;
